import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PartiesPage from "@/pages/PartiesPage";
import PartyDetailPage from "@/pages/PartyDetailPage";
import BookingPage from "@/pages/BookingPage";
import BookingsListPage from "@/pages/BookingsListPage";
import InventoryPage from "@/pages/InventoryPage";
import PlaceholderPage from "@/pages/PlaceholderPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import VehiclesPage from "@/pages/VehiclesPage";
import ReportsPage from "@/pages/ReportsPage";
import ServicesPage from "@/pages/ServicesPage";
import JobCardsPage from "@/pages/JobCardsPage";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global 401 interceptor - auto logout on token expiry
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const isAuthRoute = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register');
      if (!isAuthRoute) {
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem("token");
      if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [isAuthenticated]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]"><p className="text-slate-500">Loading...</p></div>;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <LoginPage setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/" replace />} />

        {/* Authenticated routes with Layout */}
        <Route path="/" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><DashboardPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/job-cards" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><JobCardsPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/bookings" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><BookingsListPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/bookings/new" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><BookingPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/parties" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><PartiesPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/parties/:partyId" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><PartyDetailPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/inventory" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><InventoryPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/settings" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><SuperAdminPage /></Layout> : <Navigate to="/login" replace />} />

        <Route path="/vehicles" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><VehiclesPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/reports" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><ReportsPage /></Layout> : <Navigate to="/login" replace />} />
        <Route path="/services" element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><ServicesPage /></Layout> : <Navigate to="/login" replace />} />

        {/* Placeholder routes */}
        {["/technicians"].map(path => (
          <Route key={path} path={path} element={isAuthenticated ? <Layout setIsAuthenticated={setIsAuthenticated}><PlaceholderPage /></Layout> : <Navigate to="/login" replace />} />
        ))}
      </Routes>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
